import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { Country } from '~/Api/Country';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import {
	AeMaxPrice,
	ErrorResponse,
	Item,
	ProductUpdate,
	ReferenceUpdate,
	ValidationResult,
} from '~/Api/User/ProductGroup/AeMaxPrice';
import config from '~/config';
import { handleError } from '~/ErrorHandler';
import { logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import { removeFromGroup } from '~/GroupProduct';
import {
	AeGroupTypes,
	fetchReferenceBasketVersions,
	Module,
	pruneReferenceBasketVersions,
	ReferenceBasketVersion,
	resolveAeProductGroup,
	selectReferenceBasketVersions,
	State as CzPriceReferencingState,
} from '~/PriceReferencing';
import { PricedProduct, Product as GeneralProduct, ProductPrice } from '~/Product';
import { pruneDatasets } from '~/ProductDataset';
import { pruneAllPrices, selectProductPrice, State as ProductPriceState } from '~/ProductPrice';
import { ThunkAction, ThunkDispatch } from '~/Store';

import {
	commitAeMaxPriceUpdate,
	fetchAeMaxPrice,
	handleAeMaxPriceUpdateResponse,
	handleCommitError,
	loadNationalities,
	pruneAeMaxPrice,
	scheduleAeMaxPriceUpdate,
} from './actions';
import ProductGroupAeMaxPriceContent from './AeMaxPrice';
import { selectAeMaxPrice, selectExport, selectNationalityByCode, State as AeMaxPriceState } from './reducer';

import type Product from '~/Api/Product/Product';
import { ProductRow } from './ProductsTable/ProductsTable';

const groupProducts = memoizeOne(
	(
		aeMaxPrice: AeMaxPrice | null,
		versions: ReferenceBasketVersion[],
		groupId: string,
		productPriceState: ProductPriceState
	): {
		pricedProducts: PricedProduct<Item>[];
		arabEmiratesProducts: ProductRow[];
		referenceBasketProducts: ProductRow[];
		otherProducts: ProductRow[];
		referenceBasketVersion: ReferenceBasketVersion | null;
	} => {
		const referenceBasketVersion =
			versions && aeMaxPrice
				? versions.find((version) => version.id === aeMaxPrice.reference.referenceBasketVersion) || null
				: null;
		const countries = referenceBasketVersion?.referenceCountries || [];
		const items = aeMaxPrice && referenceBasketVersion ? aeMaxPrice.products : [];
		const referenceBasketProducts: { [key in string]: ProductRow[] } = {};

		const groupedProducts = items.reduce(
			(
				groupedProducts: {
					arabEmiratesProducts: ProductRow[];
					referenceBasketProducts: ProductRow[];
					otherProducts: ProductRow[];
				},
				item
			) => {
				const group = resolveAeProductGroup(item.product, countries);
				const price = selectProductPrice(
					productPriceState,
					item.product,
					config.product.defaultPriceCurrency,
					null,
					false,
					groupId
				);

				if (group.type === AeGroupTypes.arabEmirates) {
					groupedProducts.arabEmiratesProducts.push({
						country: null,
						item,
						price,
					});
				} else if (group.type === AeGroupTypes.referenced && group.country) {
					if (referenceBasketProducts[group.country.alpha2]) {
						referenceBasketProducts[group.country.alpha2].push({
							country: group.country,
							item,
							price,
						});
					} else {
						referenceBasketProducts[group.country.alpha2] = [{ country: group.country, item, price }];
					}
				} else {
					groupedProducts.otherProducts.push({ country: null, item, price });
				}
				return groupedProducts;
			},
			{
				arabEmiratesProducts: [],
				referenceBasketProducts: [],
				otherProducts: [],
			}
		);

		if (groupedProducts.arabEmiratesProducts.length === 0) {
			groupedProducts.arabEmiratesProducts.push({
				country: new Country('AE', 'United Arab Emirates'),
				item: null,
				price: null,
			});
		}

		countries.forEach((country) => {
			const products = referenceBasketProducts[country.alpha2];
			if (products) {
				groupedProducts.referenceBasketProducts.push(...products);
			} else {
				groupedProducts.referenceBasketProducts.push({
					country,
					item: null,
					price: null,
				});
			}
		});

		const pricedProducts = [
			...groupedProducts.arabEmiratesProducts,
			...groupedProducts.referenceBasketProducts,
			...groupedProducts.otherProducts,
		]
			.filter(
				(
					basketItem
				): basketItem is ProductRow & {
					item: ProductRow['item'] & { product: Product };
				} => Boolean(basketItem.item)
			)
			.map((basketItem) => new PricedProduct(basketItem.item, basketItem.item.product, basketItem.price));

		return {
			...groupedProducts,
			pricedProducts,
			referenceBasketVersion,
		};
	}
);

interface State {
	aeMaxPrice: AeMaxPriceState;
	priceReferencing: CzPriceReferencingState;
	productPrice: ProductPriceState;
}

function mapStateToProps(
	state: State,
	{ group }: { group: UserProductGroup }
): {
	pricedProducts: PricedProduct<Item>[];
	arabEmiratesProducts: ProductRow[];
	referenceBasketProducts: ProductRow[];
	otherProducts: ProductRow[];
	aeMaxPrice: AeMaxPrice | null;
	versions: ReferenceBasketVersion[] | null;
	validationResult: ValidationResult | null;
	isFetching: boolean;
	referenceNationality: Country | null;
} & ReturnType<typeof groupProducts> {
	const aeMaxPriceState = selectAeMaxPrice(state.aeMaxPrice, group.id);
	const aeMaxPrice = aeMaxPriceState?.aeMaxPrice || null;
	const versions: ReferenceBasketVersion[] =
		selectReferenceBasketVersions(state.priceReferencing, Module.aeMaxPrice) || [];

	return {
		...groupProducts(aeMaxPrice, versions, group.id, state.productPrice),
		aeMaxPrice,
		versions,
		validationResult: selectExport(state.aeMaxPrice, group.id)?.validationResult || null,
		isFetching: Boolean(aeMaxPriceState?.isFetching),
		referenceNationality: selectNationalityByCode(
			state.aeMaxPrice,
			aeMaxPrice?.reference.countryOfOrigin.nationality || null
		),
	};
}

const actions = {
	onFetch:
		(group: UserProductGroup): ThunkAction<Promise<void>, any> =>
		async (dispatch) => {
			dispatch(fetchReferenceBasketVersions(Module.aeMaxPrice));
			dispatch(loadNationalities());

			try {
				await dispatch(commitAeMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleCommitError(error));
			}

			try {
				await dispatch(fetchAeMaxPrice(group.id));
			} catch (error) {
				if (!(error instanceof ErrorResponse)) {
					dispatch(handleError(error));
					return;
				}
				error.handleProductGroupNotFound();
				dispatch(handleError(error));
			}
		},
	onPrune:
		(group: UserProductGroup): ThunkAction<Promise<void>, any> =>
		async (dispatch) => {
			try {
				await dispatch(commitAeMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleCommitError(error));
			}
			dispatch(pruneAeMaxPrice(group.id));
			dispatch(pruneReferenceBasketVersions());
			dispatch(pruneAllPrices());
			dispatch(pruneDatasets());
		},
	onUpdate:
		(group: UserProductGroup, reference: ReferenceUpdate, products: ProductUpdate[]): ThunkAction<Promise<void>, any> =>
		async (dispatch) => {
			try {
				await dispatch(scheduleAeMaxPriceUpdate(group.id, reference, products));
			} catch (error) {
				dispatch(handleAeMaxPriceUpdateResponse(error, group.id));
			}
		},
	onSave:
		(group: UserProductGroup): ThunkAction<Promise<void>, any> =>
		async (dispatch) => {
			try {
				await dispatch(commitAeMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleAeMaxPriceUpdateResponse(error, group.id));
			}
		},
	selectNationality(name: string): ThunkAction<Country | null, any> {
		return (dispatch, getState) => {
			return (
				getState().aeMaxPrice.nationalities?.items.find((country) => {
					return country.name.localeCompare(name, country.alpha2, { sensitivity: 'base' }) === 0;
				}) || null
			);
		};
	},
	onDeleteItems:
		(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void) =>
		async (dispatch: ThunkDispatch<AnyAction>) => {
			try {
				await dispatch(removeFromGroup(group.id, products));
				logGoogleTagManagerGroupProductsRemove(group.id, products);
				if (onSuccess !== undefined) {
					onSuccess();
				}
			} catch (error) {
				dispatch(handleError(error));
			}
		},
};

export default connect(mapStateToProps, actions)(ProductGroupAeMaxPriceContent);
